import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Group, Paper, Stack, Text, Title } from "@mantine/core";
import { useHoldPass, useHolds, useReleasePass, useReleaseRetention } from "../api/maintenance";
import { ApiError } from "../api/client";
import { msToZonedText } from "../time/parse";
import { useNow } from "../time/useNow";
import { useZone } from "../time/useZone";
import { untilText } from "./reparse";
import { Chip } from "../ui/Chip";
import { StatePill } from "../ui/StatePill";
import { FeedbackLine } from "../ui/FeedbackLine";

/**
 * The clock the countdowns are read against. A minute, because that is the finest distinction
 * `untilText` draws, and a hold that lapses is dropped by the next poll rather than by a tick.
 */
const HOLD_TICK_MS = 60_000;

export function HoldsPanel() {
  const { data } = useHolds();
  const holdPass = useHoldPass();
  const releasePass = useReleasePass();
  const releaseRetention = useReleaseRetention();
  const zone = useZone();
  const [error, setError] = useState<string | null>(null);
  const now = useNow(HOLD_TICK_MS);
  const { t } = useTranslation("admin");

  if (!data) return null;
  const heldBy = new Map(data.passes.map((hold) => [hold.pass_name, hold]));

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  }

  return (
    <Stack gap="sm" mb={26} maw={900}>
      <Title order={3} fz="sm" lh={1.5} mb={4}>
        {t("maintenancePage.holdsHeading")}
      </Title>
      <Text size="xs" c="dimmed" maw="70ch" mb={14}>
        {t("maintenancePage.holdsBlurb")}
      </Text>

      <Stack gap={8} maw={900}>
        {data.holdable.map((name) => {
          const hold = heldBy.get(name);
          return (
            <Paper
              key={name}
              px={12}
              py={10}
              display="flex"
              style={{
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                flexWrap: "wrap",
                // A held pass is bordered in warn so the state reads at a glance; everything
                // else takes the default line.
                border: `1px solid ${
                  hold ? "var(--mantine-color-warn-filled)" : "var(--mantine-color-default-border)"
                }`,
              }}
            >
              <div style={{ flex: "1 1 220px", minWidth: 0, overflowWrap: "anywhere" }}>
                <Text fz={13}>
                  {t(`maintenancePage.passName.${name}`, {
                    defaultValue: name.replace("_", " "),
                  })}
                </Text>
                <Text fz={12} c="dimmed" mt={2}>
                  {t(`maintenancePage.passBlurb.${name}`, { defaultValue: "" })}
                </Text>
              </div>
              <Group gap={6}>
                {hold ? (
                  <>
                    <StatePill
                      tone="live"
                      title={t("maintenancePage.holdUntil", {
                        when: msToZonedText(Date.parse(hold.until), zone.name),
                      })}
                    >
                      {untilText(hold.until, now, t)}
                    </StatePill>
                    <Chip onClick={() => void act(() => releasePass.mutateAsync(name))}>
                      {t("maintenancePage.resume")}
                    </Chip>
                  </>
                ) : (
                  data.suggested_hours.map((hours) => (
                    <Chip
                      key={hours}
                      onClick={() =>
                        void act(() => holdPass.mutateAsync({ pass_name: name, hours }))
                      }
                    >
                      {t("maintenancePage.hoursChip", { count: hours })}
                    </Chip>
                  ))
                )}
              </Group>
            </Paper>
          );
        })}
      </Stack>

      {data.retention.length > 0 && (
        <>
          <Title order={3} fz="sm" lh={1.5} mb={4}>
            {t("maintenancePage.retentionHeading")}
          </Title>
          <Text size="xs" c="dimmed" maw="70ch" mb={14}>
            {t("maintenancePage.retentionBlurb")}
          </Text>
          <Stack component="ul" gap={6} fz={12} m={0} p={0} style={{ listStyle: "none" }}>
            {data.retention.map((hold) => (
              <Group component="li" key={hold.id} gap={10}>
                <span>
                  {msToZonedText(Date.parse(hold.t_start), zone.name)} →{" "}
                  {msToZonedText(Date.parse(hold.t_end), zone.name)}
                </span>
                <StatePill tone="quiet">{untilText(hold.until, now, t)}</StatePill>
                {hold.reason && (
                  <Text component="span" fz={12} c="dimmed">
                    {hold.reason}
                  </Text>
                )}
                <Chip onClick={() => void act(() => releaseRetention.mutateAsync(hold.id))}>
                  {t("maintenancePage.release")}
                </Chip>
              </Group>
            ))}
          </Stack>
        </>
      )}

      <FeedbackLine role="alert">{error}</FeedbackLine>
    </Stack>
  );
}
